type LookupFunction<T = any> = (list: any[]) => Promise<T[] | null>;
export type LookupConfig = {
	fetch: LookupFunction; // function to fetch lookup data
	sourceKey: string; // field in the main data to match against
	fallbackSourceKey?: string; // field to match against when sourceKey is empty
	targetKey?: string; // field in the lookup data to match
	outputKey: string; // name of the new field to add to main data
	transform?: (item: any) => any; // optional transform function
};

// MongoDB ObjectId validation regex (24 hex characters)
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

export function isValidObjectId(id: any): boolean {
	if (typeof id !== "string") return false;
	return OBJECT_ID_REGEX.test(id);
}

export function getLookupSourceValue(
	item: any,
	{ sourceKey, fallbackSourceKey }: LookupConfig
) {
	const value = item[sourceKey];
	return !value && fallbackSourceKey ? item[fallbackSourceKey] : value;
}

// The lookup item whose targetKey matches the value, ignoring case.
function findLookupMatch(lookupArray: any[], targetKey: string, value: any) {
	return lookupArray.find((lookupItem: any) => {
		if (value == null) return false;
		return (
			String(lookupItem?.[targetKey]).toLowerCase() ===
			String(value).toLowerCase()
		);
	});
}

// The items with each config's looked-up value in its outputKey, from what
// findUniqueIds returned. A value the lookup can't name keeps what the item
// already has in that key.
export function applyLookup(
	data: any[],
	lookupData: Record<string, any[]>,
	configs: LookupConfig[]
) {
	return data.map((item: any) => {
		const newItem: any = { ...item };

		configs.forEach((config) => {
			const { sourceKey, targetKey = "_id", outputKey, transform } = config;
			const lookupArray = lookupData[sourceKey] ?? [];
			const sourceValue = getLookupSourceValue(item, config);

			const findMatch = (value: any) =>
				findLookupMatch(lookupArray, targetKey, value);

			// A value without a match looks up as transform(undefined), or as
			// undefined without a transform. A list keeps that entry; a single value
			// keeps the item's own value instead (below).

			// if the source value is an array, then we need to loop through the array and add the lookup data to the new item
			if (Array.isArray(sourceValue)) {
				newItem[outputKey] = [];
				sourceValue.forEach((sourceItem: any) => {
					const match = findMatch(sourceItem);
					const lookedUpValue = transform ? transform(match) : match?.name;
					newItem[outputKey].push(lookedUpValue);
				});
			} else {
				const match = findMatch(sourceValue);
				const lookedUpValue = transform ? transform(match) : match?.name;

				// Preserve the original value if the lookup returns undefined/null
				// and the original item already has a value for this field.
				if (
					(lookedUpValue !== undefined && lookedUpValue !== null) ||
					item[outputKey] === undefined ||
					item[outputKey] === null
				) {
					newItem[outputKey] = lookedUpValue;
				}
				// otherwise keep the original item[outputKey]
			}
		});

		return newItem;
	});
}

export async function findUniqueIds(data: any[], configs: LookupConfig[]) {
	if (!data?.length || !configs?.length) return {};

	const uniqueIdsMap: Record<string, any[]> = {};

	configs.forEach((config) => {
		const ids = data
			.flatMap((item) => getLookupSourceValue(item, config) || [])
			.filter((id) => isValidObjectId(id));

		uniqueIdsMap[config.sourceKey] = [...new Set(ids.map(String))];
	});

	// Deduplicate fetches by sourceKey: if multiple configs share the same sourceKey,
	// only fetch once and reuse the results for all related configs.
	const uniqueSourceKeys = Array.from(new Set(configs.map((c) => c.sourceKey)));

	// For each unique sourceKey, pick the first matching config's fetch function
	const sourceKeyToFetch: Record<string, LookupFunction> = {};
	for (const sourceKey of uniqueSourceKeys) {
		const cfg = configs.find((c) => c.sourceKey === sourceKey);
		if (cfg) {
			sourceKeyToFetch[sourceKey] = cfg.fetch;
		}
	}

	const resultEntries = await Promise.all(
		uniqueSourceKeys.map(async (sourceKey) => {
			const fetchFn = sourceKeyToFetch[sourceKey];
			try {
				const res = await fetchFn(uniqueIdsMap[sourceKey] || []);
				return [sourceKey, res?.filter(Boolean) ?? []];
			} catch (err) {
				console.error(`Lookup fetch failed for ${sourceKey}`, err);
				return [sourceKey, []];
			}
		})
	);

	return Object.fromEntries(resultEntries);
}
